import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, Briefcase, TrendingUp, Users, Award } from 'lucide-react';
import WorkExperienceCard from '../components/WorkExperienceCard';
import { Container } from '../components/layout/container';
import { getWorkExperienceData } from '../data/workExperience';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const statIcons = [Briefcase, TrendingUp, Users, Award];

const WorkExperiencePage = () => {
  const { language, t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');

  const experiences = useMemo(() => getWorkExperienceData(language), [language]);

  const allTags = useMemo(() => {
    const tags = new Set();
    experiences.forEach(exp => {
      exp.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, [experiences]);

  const filteredExperiences = useMemo(() => {
    return experiences.filter(exp => {
      const matchesSearch = searchTerm === '' || 
        exp.companyDisplay.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exp.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exp.summary.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTag = selectedTag === 'all' || exp.tags.includes(selectedTag);
      return matchesSearch && matchesTag;
    });
  }, [experiences, searchTerm, selectedTag]);

  const statsList = useMemo(() => {
    const baseStats = t.experiencePage?.stats || [
      { label: 'Years Experience', value: '10+' },
      { label: 'Products Shipped', value: '25+' },
      { label: 'Users Impacted', value: '5M+' },
      { label: 'Companies', value: '7' },
    ];
    return baseStats.map((stat, idx) => ({
      ...stat,
      icon: statIcons[idx] || Briefcase,
    }));
  }, [t]);

  return (
    <div className="min-h-screen bg-[#0B0B0F] overflow-hidden">
      <SEO 
        title={t.experiencePage?.seo?.title || "Work Experience - Nandan Priyadarshi | 10+ Years in Product Management"}
        description={t.experiencePage?.seo?.description || "10+ years in AI, data and B2B SaaS product management: Xiaomi, FlashLabs, Wemero, TradeBees and Wipro. 100% adoption across 5 markets, 280% transaction growth."}
        canonicalUrl="/experience"
        keywords={t.experiencePage?.seo?.keywords || "Product Manager Experience, Senior PM Career, Xiaomi Product Manager, AI Product Development, Product Management Portfolio"}
      />
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-slate-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-indigo-500/8 rounded-full blur-[100px]" />
      </div>

      {/* Navigation */}

      {/* Hero Section */}
      <section className="relative pt-16 pb-20">
        <Container>
          <div className="text-center max-w-4xl mx-auto">
            {/* Eyebrow */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
                {t.experiencePage?.availableBadge || 'Available for Opportunities'}
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <span className="bg-gradient-to-r from-white via-white to-white/60 bg-clip-text text-transparent">
                {t.experiencePage?.titleLine1 || 'A Decade of Building'}
              </span>
              <br />
              <span className="bg-gradient-to-r from-slate-300 via-slate-400 to-slate-500 bg-clip-text text-transparent">
                {t.experiencePage?.titleLine2 || 'Products That Matter'}
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              className="text-lg sm:text-xl text-white/50 max-w-2xl mx-auto mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t.experiencePage?.subtitle || 'From data engineering foundations to leading AI-powered commercial platforms, every role shaped my approach to shipping with impact.'}
            </motion.p>

            {/* Stats Grid */}
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {statsList.map((stat) => (
                <motion.div
                  key={stat.label}
                  className="group relative p-6 rounded-2xl bg-gradient-to-b from-white/[0.08] to-white/[0.02] border border-white/10 hover:border-white/20 transition-[transform,background-color,border-color] duration-200"
                  whileHover={{ y: -4, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  {/* Glow effect */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-slate-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative">
                    <stat.icon className="h-5 w-5 text-slate-400 mb-3" />
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-xs text-white/50 uppercase tracking-wider">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Timeline Divider */}
      <div className="relative py-8">
        <Container>
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
              <Briefcase className="h-4 w-4 text-slate-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-white/60">
                {t.experiencePage?.timelineTitle || 'Journey Timeline'}
              </span>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          </div>
        </Container>
      </div>

      {/* Filters Section */}
      <section className="py-8">
        <Container>
          <motion.div
            className="max-w-4xl mx-auto space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {/* Search */}
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-slate-500/20 to-indigo-500/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500" />
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/30" />
                <input
                  type="text"
                  placeholder={t.experiencePage?.searchPlaceholder || "Search by company, role, or keywords..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white/[0.03] border border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:outline-none focus:border-white/20 focus:bg-white/[0.05] transition-[background-color,border-color,box-shadow] duration-150"
                />
              </div>
            </div>

            {/* Tags */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Filter className="h-4 w-4 text-white/40" />
                <span className="text-sm font-medium text-white/50">
                  {t.experiencePage?.filterBy || 'Filter by expertise'}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <motion.button
                  onClick={() => setSelectedTag('all')}
                  className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-[transform,background-color,border-color,color] duration-150 ${
                    selectedTag === 'all'
                      ? 'bg-white text-[#0B0B0F] shadow-lg shadow-white/20'
                      : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 hover:text-white hover:border-white/20'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {t.experiencePage?.allFilter || 'All'}
                </motion.button>
                {allTags.map(tag => (
                  <motion.button
                    key={tag}
                    onClick={() => setSelectedTag(tag)}
                    className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-[transform,background-color,border-color,color] duration-150 ${
                      selectedTag === tag
                        ? 'bg-white text-[#0B0B0F] shadow-lg shadow-white/20'
                        : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 hover:text-white hover:border-white/20'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {tag}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Results count */}
            <div className="flex items-center justify-between text-sm text-white/40">
              <span>
                {(t.experiencePage?.showingCount || 'Showing {filtered} of {total} experiences')
                  .replace('{filtered}', filteredExperiences.length)
                  .replace('{total}', experiences.length)}
              </span>
              {(searchTerm || selectedTag !== 'all') && (
                <button
                  onClick={() => { setSearchTerm(''); setSelectedTag('all'); }}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  {t.experiencePage?.clearFilters || 'Clear filters'}
                </button>
              )}
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Experiences Grid */}
      <section className="py-12 pb-24">
        <Container>
          <div className="max-w-6xl mx-auto">
            <AnimatePresence mode="wait">
              {filteredExperiences.length > 0 ? (
                <motion.div
                  className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {filteredExperiences.map((experience, index) => (
                    <WorkExperienceCard
                      key={experience.id}
                      experience={experience}
                      index={index}
                    />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  className="text-center py-20"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center">
                    <Search className="h-8 w-8 text-white/30" />
                  </div>
                  <p className="text-white/50 mb-2">
                    {t.experiencePage?.noResults || 'No experiences found'}
                  </p>
                  <p className="text-sm text-white/30">
                    {t.experiencePage?.adjustFilters || 'Try adjusting your search or filters'}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default WorkExperiencePage;
